// src/pages/Login/Login.tsx
import React, { useState, useEffect } from 'react';
import { Box, Button, Input, Heading, VStack, useToast } from '@chakra-ui/react';
import axios from 'axios';
import Cookies from 'js-cookie';

interface LoginProps {
  onLogin: (token: string) => void;
}

const BASE_URL = import.meta.env.VITE_BASE_SERVER_URL as string;
const SITE_KEY = import.meta.env.VITE_SITE_KEY as string;

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const toast = useToast();

  useEffect(() => {
    // 1) Inject the reCAPTCHA script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // 2) Remove the script on unmount
      document.body.removeChild(script);

      // 3) Remove the injected badge element
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge && badge.parentNode) {
        badge.parentNode.removeChild(badge);
      }
    };
  }, []);

  const handleLogin = async () => {
    if (!(window as any).grecaptcha) {
      toast({
        title: 'reCAPTCHA not ready',
        description: 'Please wait a moment and try again.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Execute reCAPTCHA, then post credentials + token
    (window as any).grecaptcha.ready(() => {
      (window as any).grecaptcha
        .execute(SITE_KEY, { action: 'login' })
        .then(async (recaptchaToken: string) => {
          try {
            const response = await axios.post(`${BASE_URL}/login`, {
              username,
              password,
              recaptchaToken,
            });
            const accessToken = response.data.access_token;

            // Store cookie for 12 hours
            Cookies.set('accessToken', accessToken, {
              expires: 0.5, // half a day
              secure: true,
              sameSite: 'Strict',
            });

            toast({
              title: 'Login Successful',
              description: 'Welcome back!',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
            onLogin(accessToken);
          } catch (error: any) {
            toast({
              title: 'Login Failed',
              description: error.response?.data?.detail || 'Invalid username or password',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
          }
        })
        .catch((err: any) => {
          toast({
            title: 'reCAPTCHA Error',
            description: 'Unable to verify reCAPTCHA. Please try again.',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          console.error('grecaptcha.execute error', err);
        });
    });
  };

  return (
    <Box
      className="login-page"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      height="100vh"
      bg="gray.50"
      px={4}
    >
      <VStack
        spacing={4}
        bg="white"
        p={8}
        boxShadow="lg"
        rounded="md"
        width={{ base: '90%', md: '400px' }}
      >
        <Heading as="h1" size="lg" color="blue.500">
          Login
        </Heading>
        <Input
          placeholder="Username"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          focusBorderColor="blue.500"
          variant="outline"
          size="lg"
        />
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          focusBorderColor="blue.500"
          variant="outline"
          size="lg"
        />
        <Button colorScheme="blue" size="lg" width="100%" onClick={handleLogin}>
          Login
        </Button>
      </VStack>
    </Box>
  );
};

export default Login;
